export class ApiError extends Error {
	constructor(
		public status: number,
		public code: string,
		message: string,
		public body?: unknown,
		public retryAfterSeconds?: number,
	) {
		super(message);
		this.name = "ApiError";
	}
}

export interface ApiRequestInit extends RequestInit {
	timeoutMs?: number;
}

export function requestTimeoutMs(path: string, init: RequestInit): number {
	if (/\/render(?:\/pptx)?(?:\?|$)/.test(path)) return 600_000;
	if (init.body instanceof FormData) return 180_000;
	const method = (init.method ?? "GET").toUpperCase();
	return method === "GET" || method === "HEAD" ? 30_000 : 180_000;
}

function retryAfterSeconds(value: string | null): number | undefined {
	if (!value?.trim()) return undefined;
	const seconds = Number(value);
	if (Number.isFinite(seconds)) return seconds >= 0 ? seconds : undefined;
	const until = Date.parse(value);
	return Number.isFinite(until)
		? Math.max(0, Math.ceil((until - Date.now()) / 1000))
		: undefined;
}

function errorDetails(body: unknown): { code?: string; message?: string } {
	if (!body || typeof body !== "object" || !("error" in body)) return {};
	const error = body.error;
	if (!error || typeof error !== "object") return {};
	return {
		code:
			"code" in error && typeof error.code === "string"
				? error.code
				: undefined,
		message:
			"message" in error && typeof error.message === "string"
				? error.message
				: undefined,
	};
}

const unconfirmedMutationMessage =
	"Couldn't confirm the request's outcome. It may still complete; check before sending it again.";

async function parseResponse<T>(
	response: Response,
	mutation: boolean,
): Promise<T> {
	if (response.status === 204 || response.status === 205) return undefined as T;
	let text: string;
	try {
		text = await response.text();
	} catch {
		throw new ApiError(
			response.status,
			"response_read_error",
			mutation
				? unconfirmedMutationMessage
				: "Couldn't read the service response. Please try again.",
			undefined,
			retryAfterSeconds(response.headers.get("retry-after")),
		);
	}
	let body: unknown;
	let validJson = false;
	try {
		body = JSON.parse(text);
		validJson = true;
	} catch {
		body = undefined;
	}
	if (!response.ok) {
		const details = errorDetails(body);
		const unconfirmed =
			mutation &&
			(response.status >= 500 || !details.code || details.code === "unknown");
		throw new ApiError(
			response.status,
			unconfirmed ? "unconfirmed_mutation" : details.code ?? "unknown",
			unconfirmed
				? unconfirmedMutationMessage
				: details.message ??
					`The service returned an error (${response.status}). Please try again.`,
			validJson ? body : undefined,
			retryAfterSeconds(response.headers.get("retry-after")),
		);
	}
	if (!validJson) {
		throw new ApiError(
			response.status,
			"invalid_response",
			mutation
				? unconfirmedMutationMessage
				: "The service returned an invalid response. Please try again.",
		);
	}
	return body as T;
}

export async function request<T>(
	path: string,
	init: ApiRequestInit = {},
): Promise<T> {
	const {
		timeoutMs = requestTimeoutMs(path, init),
		signal: callerSignal,
		...options
	} = init;
	if (
		!Number.isFinite(timeoutMs) ||
		timeoutMs <= 0 ||
		timeoutMs > 2_147_483_647
	) {
		throw new RangeError(
			"Request timeout must be a positive finite timer duration",
		);
	}
	callerSignal?.throwIfAborted();
	const controller = new AbortController();
	const abortFromCaller = () => controller.abort(callerSignal?.reason);
	callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
	const method = (options.method ?? "GET").toUpperCase();
	const mutation = method !== "GET" && method !== "HEAD";
	let response: Response | undefined;
	const timeout = setTimeout(() => {
		controller.abort(
			new ApiError(
				response?.status ?? 0,
				"timeout",
				method === "GET" || method === "HEAD"
					? "The service took too long to respond. Please try again."
					: "The request timed out. It may still complete; check before sending it again.",
				undefined,
				retryAfterSeconds(response?.headers.get("retry-after") ?? null),
			),
		);
	}, timeoutMs);
	let rejectAborted: () => void = () => {};
	const aborted = new Promise<never>((_, reject) => {
		rejectAborted = () => reject(controller.signal.reason);
		controller.signal.addEventListener("abort", rejectAborted, { once: true });
	});
	try {
		const headers = new Headers(options.headers);
		if (options.body instanceof FormData) {
			headers.delete("Content-Type");
		} else if (!headers.has("Content-Type")) {
			headers.set("Content-Type", "application/json");
		}
		const work = (async () => {
			response = await fetch(`/api${path}`, {
				credentials: "include",
				...options,
				headers,
				signal: controller.signal,
			});
			return parseResponse<T>(response, mutation);
		})();
		return await Promise.race([work, aborted]);
	} catch (error) {
		if (controller.signal.aborted) throw controller.signal.reason;
		if (error instanceof ApiError) throw error;
		throw new ApiError(
			0,
			"network_error",
			mutation
				? unconfirmedMutationMessage
				: "Couldn't reach the service. Check your connection and try again.",
		);
	} finally {
		clearTimeout(timeout);
		callerSignal?.removeEventListener("abort", abortFromCaller);
		controller.signal.removeEventListener("abort", rejectAborted);
	}
}

export function requestForm<T>(
	path: string,
	form: FormData,
	init: ApiRequestInit = {},
): Promise<T> {
	return request<T>(path, { ...init, method: "POST", body: form });
}
